import React, { Component, useState } from "react";
import MonitoringIllustration from "./assets/Monitoring.png";
import Apdex from "./assets/apdex.png";
export default function Monitoring() {
  return (
    <div>
      <article id="1ca40484-2295-8072-a8cb-c53daef8f184" className="page sans">
        <div className="page-body">
          <p id="1cb40484-2295-8056-b01c-c91c352ac668" className="">
            Hey everyone! It’s been a while. I’ve had a pretty good reason for
            my absence — I’ve swapped waking up from my team’s nightly PagerDuty
            alarms for waking up from something far more delightful: my
            beautiful 3-month-old baby girl.
          </p>
          <p id="1d040484-2295-8096-b10a-dbffb3bbcd1b" className="">
            The comparison between a crying baby and a PagerDuty alert isn’t
            coincidental: In both cases, I go through the same emotional
            rollercoaster: denial, bargaining, and acceptance — all within a
            matter of minutes. With PagerDuty, it goes something like: “Maybe
            it’s a false alarm?”, “Maybe the error only happened once and will
            magically resolve if I click ‘acknowledge’?”, “Oh crap, I guess I
            really have to get up.” With the baby: “Did I hear that right?”,
            “Maybe if I wait a second she’ll settle back to sleep?”, “Oh crap, I
            really have to get up.” (Literally... crap, in the crying-baby
            example)
          </p>
          <p id="1cb40484-2295-8085-bf78-c38ec3573740" className="">
            Over the past year, I’ve worked hard to improve our on-call
            experience — making alerts more accurate so no one loses sleep over
            false alarms. Along the way, I’ve learned a lot about what’s truly
            worth monitoring. So here it is: what you should be tracking in your
            app to make sure you’re only waking up for{" "}
            <strong>the things that really matter.</strong>
          </p>
          <h4 id="1cb40484-2295-801a-bed7-fee34d37e3ae" className="">
            What Does Good Monitoring Look Like?
          </h4>
          <p id="1d040484-2295-80d8-8b20-c0ad5b301413" className="">
            If you’re waking up in the middle of the night, it better be for a
            damn good reason. You want to be alerted only when something serious
            happens — not for minor bugs or things that can wait until morning.
            However, you want to know as soon as possible when things go bad.
            Ideally, you’d be alerted{" "}
            <strong>before your users notice anything wrong</strong>.
          </p>
          <p id="1d040484-2295-8020-a1f4-d59a03988cf9" className="">
            The good news? That’s totally doable, if you focus on the right
            metrics and make sure every alert includes enough context so anyone
            on-call can quickly take action — even when half-asleep.
          </p>
          <p id="1d040484-2295-80f2-b1ba-c74f9253d406" className="">
            The bad news? Setting up the right alerts is{" "}
            <strong>not a one-and-done task</strong>. It’s an ongoing effort.
            You’ll need to keep adjusting thresholds, adding and removing
            alerts, and continuously refining your monitoring setup — just like
            maintaining your codebase or sharing knowledge across the team.
          </p>
          <h4 id="1cb40484-2295-8098-a51a-ccd4375eb7a4" className="">
            Key Metrics to Monitor
          </h4>
          <p id="1cb40484-2295-80fc-a988-d5eb3cdf5871" className="">
            There are tons of metrics you <em>could</em> monitor — but let’s
            focus on the ones that really move the needle. We’ll start with
            low-level system metrics and work our way up to high-level user
            behavior.
          </p>
          <img
            src={MonitoringIllustration}
            style={{ width: "100%", marginBottom: 30 }}
          />
          <p id="1cb40484-2295-807d-b6d0-d032bf59fc56" className="">
            <strong>System Metrics (CPU, Disk Usage, etc.)</strong>
          </p>
          <p id="1d040484-2295-80f0-9f52-e20b28b7fc38" className="">
            Before diving into application-specific monitoring, you need to
            ensure the underlying system is healthy.
          </p>
          <p id="1d040484-2295-80ca-b9bb-d66d0a960f82" className="">
            Metrics like high CPU usage, memory pressure, or low disk space can
            lead to unexpected behavior, crashes, or even data loss.
          </p>
          <p id="1d040484-2295-80ed-a2ed-e817d4bab656" className="">
            Set up alerts for these so you can{" "}
            <strong>scale up resources</strong> or{" "}
            <strong>apply hotfixes</strong> before users feel the impact. If
            auto-scaling is an option — and it fits within your cost constraints
            — even better. (Think AWS SQS queue size, DB storage, etc.) That
            way, you’re only alerted when manual intervention is really needed.
          </p>
          <p id="1d040484-2295-80f5-9279-f71fb5548d0d" className="">
            These metrics are usually collected via a monitoring agent on the
            machine, like <code>node_exporter</code> with{" "}
            <strong>Prometheus</strong>.
          </p>
          <p id="1cc40484-2295-808b-8412-dca6b900c551" className="">
            <strong>Service health (AKA “Liveness”)</strong>
          </p>
          <p id="1d040484-2295-80a7-bc86-c692d83fa4f2" className="">
            This one’s non-negotiable. You need to know whether your service is{" "}
            <em>actually up</em>.
          </p>
          <p id="1d040484-2295-8073-aafb-d1c78478bfd6" className="">
            This is typically done by exposing a <code>/live</code> or{" "}
            <code>/health</code> endpoint and polling it regularly. Tools like{" "}
            <a href="https://uptimerobot.com/">UptimeRobot</a>,{" "}
            <a href="https://betterstack.com/">BetterUptime</a>, and{" "}
            <a href="https://www.site24x7.com/">Site24x7</a> can handle the
            health monitoring for you.
          </p>
          <p id="1d240484-2295-80c7-8ada-ee4c93701028" className="">
            If your services are running on Kubernetes, you probably know the{" "}
            <strong>liveness</strong> configuration. When using{" "}
            <code>livenessProbe</code>, Kubernetes periodically checks if your
            service is up (by sending an HTTP request, checking if a port is
            open, or executing a command), and even restarts it if the probe
            fails a few times, making it proactive.
          </p>
          <link
            rel="stylesheet"
            href="https://cdnjs.cloudflare.com/ajax/libs/prism/1.29.0/themes/prism.min.css"
            integrity="sha512-tN7Ec6zAFaVSG3TpNAKtk4DOHNpSwKHxxrsiw4GHKESGPs5njn/0sMCUMl2svV4wo4BK/rCP7juYz+zx+l6oeQ=="
            crossOrigin="anonymous"
            referrerPolicy="no-referrer"
          />
          <pre id="1d240484-2295-8003-b29f-f19ea74815e1" className="code">
            <code
              className="language-YAML"
              style={{ whiteSpace: "pre-wrap", wordBreak: "break-all" }}
            >
              livenessProbe:{"\n"}
              {"\t"}httpGet:{"\n"}
              {"\t"}
              {"\t"}path: /health{"\n"}
              {"\t"}
              {"\t"}port: 8080
            </code>
          </pre>
          <p id="1cb40484-2295-8041-b7d1-d1211426e722" className="">
            <strong>Application Performance (APM)</strong>
          </p>
          <p id="1d040484-2295-8097-bd22-ec6ca5ee2f47" className="">
            Application Performance Monitoring is a set of tools that help to
            track the performance and availability of applications.
          </p>
          <p id="1d040484-2295-8082-addb-c7694ed7b22d" className="">
            {" "}
            APM is a big deal — and for a really good reason, It helps you
            understand how your app is performing in real-time. Here are the key
            APM metrics you should alert on:
          </p>
          <ul
            id="1cd40484-2295-8022-8a60-c4a9ca1afd16"
            className="bulleted-list"
          >
            <li style={{ listStyleType: "disc" }}>
              <strong>Apdex</strong>
              <p id="1d040484-2295-8080-baae-cb44c252f3b9" className="">
                Application Performance Index, is a metric that measures
                <strong> user satisfaction with our app’s performance</strong>.
                It’s a score from 0 to 1, where 0 is total frustration and 1 is
                total satisfaction. It’s user-friendly and easy to understand.
                The computation is based on a threshold (T), and the number of
                requests in a period where the response time was ≤ T (This is a
                simplified explanation). Most APM tools (Datadog, New Relic,
                Grafana) support this out of the box. Define a reasonable
                threshold — and alert when things drop.
              </p>
              <img src={Apdex} style={{ width: "100%", marginBottom: 30 }} />
            </li>
          </ul>
          <ul
            id="1cd40484-2295-80e6-8924-c7fddf597971"
            className="bulleted-list"
          >
            <li style={{ listStyleType: "disc" }}>
              <strong>High Error Rate (5xx, usually)</strong>: <br />
              Spikes in error rates often signal a problem. Most monitoring
              tools support anomaly detection for these. In Prometheus, for
              example, you can use <br />
              <code>
                increase(..{"{"}status="5.."{"}"})
              </code>{" "}
              to identify trends.
              <p id="1cd40484-2295-8077-833a-df979b836ebd" className="">
                4xx errors are less commonly monitored, but may be useful for
                spotting abnormal client behavior.
              </p>
            </li>
          </ul>
          <ul
            id="1cd40484-2295-809f-96c9-dcbceb822ea2"
            className="bulleted-list"
          >
            <li style={{ listStyleType: "disc" }}>
              <strong>Key Request / Key Transaction</strong>
              <br />
              Some endpoints are business-critical — like <br />
              <code>/login</code> or <code>/payment</code>. Many APMs let you
              monitor these <strong>separately</strong>, with their own
              thresholds and alerts, so they never slip through the cracks.
            </li>
          </ul>
          <p id="1cb40484-2295-8094-a097-cd110d7a7b44" className="">
            <strong>
              User Behavior (Synthetics Canaries / Production tests)
            </strong>
          </p>
          <p id="1d040484-2295-8002-9944-d44fcb4e8531" className="">
            Synthetic (or active) monitoring simulates{" "}
            <strong>full user flows</strong> in production, by running
            “Canaries” - automated scripts that mimic user actions by calling
            API requests and validating their response, etc. You can think of
            synthetics as running a bit of your CI suite continuously, in
            production.
          </p>
          <p id="1d040484-2295-80ba-a891-e57b65a54f7e" className="">
            Use it for high-value flows only, since it's more
            resource-intensive. Synthetic checks can catch issues before users
            even notice — making them a powerful <strong>proactive</strong>{" "}
            tool.
          </p>
          <p id="1d240484-2295-809b-b958-d6da9a28d1c2" className="">
            You can utilize Synthetic Monitoring in AWS (CloudWatch), Datadog,
            New Relic, and more.
          </p>
          <p id="1d240484-2295-8060-826d-eeea9066c7d4" className=""></p>
          <p id="1cb40484-2295-80f7-bf4f-f5174fd881fc" className="">
            <strong>Bonus: Frontend / Web Monitoring</strong>
          </p>
          <p id="1d040484-2295-801b-a024-ccfacae5d89f" className="">
            Frontend monitoring is its own beast. Why? Because the frontend runs
            on the user's device — not on your infrastructure. That means{" "}
            <strong>
              you have to explicitly send logs back to your servers
            </strong>
            .
          </p>
          <p id="1d040484-2295-8036-a459-c4ce3ba1ab4d" className="">
            Thankfully, most APM tools offer client-side plugins to
            automatically capture:
          </p>
          <ul
            id="1d040484-2295-80f8-8af1-db8160037509"
            className="bulleted-list"
          >
            <li style={{ listStyleType: "disc" }}>JavaScript errors</li>
          </ul>
          <ul
            id="1d040484-2295-8038-9e22-f86ec3624e9a"
            className="bulleted-list"
          >
            <li style={{ listStyleType: "disc" }}>Network errors</li>
          </ul>
          <ul
            id="1d040484-2295-80e2-8a8a-e0cb156c273f"
            className="bulleted-list"
          >
            <li style={{ listStyleType: "disc" }}>Web performance stats</li>
          </ul>
          <ul
            id="1d040484-2295-80b9-9adc-d4977c46d1e0"
            className="bulleted-list"
          >
            <li style={{ listStyleType: "disc" }}>And more</li>
          </ul>
          <p id="1d040484-2295-8092-888c-e170596301de" className="">
            Still, frontend issues can be tricky to detect because they often
            boil down to user experience. (“I clicked the button and nothing
            happened.”)
          </p>
          <p id="1d040484-2295-80b3-8a25-e8eab3c2c5e6" className="">
            Here’s what you should track:
          </p>
          <p id="1cb40484-2295-8028-9b6e-da4e9795e03c" className="">
            <strong>Web Vitals</strong>
          </p>
          <p id="1d040484-2295-80e1-9360-d710b924aa04" className="">
            Web Vitals (by Google) aim to{" "}
            <strong>quantify user experience</strong> — things like UI
            responsiveness and visual stability.
          </p>
          <p id="1d040484-2295-804d-ac03-dc778fbf34a5" className="">
            Some key metrics to know:
          </p>
          <ul
            id="1d040484-2295-80fa-aabe-ecc19d8aea6a"
            className="bulleted-list"
          >
            <li style={{ listStyleType: "disc" }}>
              <strong>LCP (Largest Contentful Paint)</strong> — how quickly the
              main content loads
            </li>
          </ul>
          <ul
            id="1d040484-2295-80e7-9c40-dcb1d487aebf"
            className="bulleted-list"
          >
            <li style={{ listStyleType: "disc" }}>
              <strong>CLS (Cumulative Layout Shift)</strong> — how much the
              layout jumps around
            </li>
          </ul>
          <p id="1d040484-2295-8018-a230-c5ca97436cc9" className="">
            Most frontend monitoring tools support Web Vitals, or at least a
            subset. More info here:{" "}
            <a href="https://web.dev/articles/vitals">web.dev/vitals</a>
          </p>
          <p id="1cb40484-2295-80f8-9058-eb558305390c" className="">
            <strong>Error Rate</strong>
          </p>
          <p id="1d040484-2295-802d-ad97-e7177d0889bb" className="">
            Track <strong>trends </strong>in JavaScript errors — things like{" "}
            <code>TypeError</code>, <code>NetworkError</code>, etc. This can
            help you catch bugs before users report them.
          </p>
          <p id="1d040484-2295-802d-8a59-c28610bf5aca" className="">
            Heads up: You might get<strong> duplicate alerts</strong> here if a
            client-side error reflects an existing backend issue.
          </p>
          <p id="1cb40484-2295-8084-b7ad-f7ac785de5c5" className="">
            <strong>Browser </strong>
            <strong>Canaries</strong>
          </p>
          <p id="1d040484-2295-801a-83cd-e82cdb8ea245" className="">
            Some monitoring tools like AWS CloudWatch, let you define a{" "}
            <a href="https://docs.aws.amazon.com/AmazonCloudWatch/latest/monitoring/CloudWatch_Synthetics_Canaries.html">
              Canary with access to a headless Google Chrome Browser
            </a>
            . This way, we can simulate real user actions
            <strong> in the browser </strong>(think Selenium or Puppeteer).
          </p>
          <p id="1d040484-2295-809d-8c1f-d635bdb949e6" className="">
            They’re great for validating key journeys (like login or checkout)
            end to end — and catching issues before users hit them.
          </p>
          <h4 id="1cb40484-2295-8045-9549-ea0a1b7d6f03" className="">
            Summary
          </h4>
          <p id="1d040484-2295-8073-af2a-fe1931d09504" className="">
            We covered the most important metrics to monitor in your app so that
            your alerts are meaningful, actionable, and worth waking up for.
          </p>
          <p id="1d040484-2295-8006-9181-ea168b5fde99" className="">
            It’s not all or nothing — even starting with a few of these will
            improve your setup dramatically.
          </p>
          <p id="1d040484-2295-800b-bb8d-edc8ea2ed6f0" className="">
            Just remember: <strong>monitoring is never done</strong>. Keep
            tweaking those thresholds, refining alerts, and removing the noisy
            ones. Your future self (and your sleep schedule) will thank you —
            and so will your users.
          </p>
          <p id="1d040484-2295-8042-811c-e4dd1750466d" className="">
            Sleep tight,
          </p>
          <p id="1d040484-2295-80ce-b0e8-dcfeff402ac9" className="">
            Omer
          </p>
          <p id="1cb40484-2295-80cf-b004-d991e3a4dc29" className=""></p>
        </div>
      </article>
    </div>
  );
}
